import React, { useEffect, useMemo, useState } from 'react';
import { BucketBet, getBucketBets } from '../api/strategy';
import './StrategyReports.css';

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const codes: Record<string, string> = {
  'ON_BASE|S1|GENUINE':'OB1', 'ON_BASE|S1|BOOTSTRAP':'OB-B', 'ON_BASE|S4|':'OB4',
  'VALUE|S2|HIGH_REL':'V2', 'VALUE|S2|LOW_REL':'V2L', 'VALUE|S4|':'V4', 'VALUE_S4_SETUP_TOP2|S4|':'VS2',
  'FALSE_FAV|S12|':'FF', 'INRUN_CORE1|S-|':'IR', 'INRUN_STABLE5_CORE1|S-|':'ST5', 'INRUN_UNIQUE|S-|':'IU',
  'EDGE_ROUTER_WIN|S-|':'EW', 'EDGE_ROUTER_PLACE|S-|':'EP', 'EDGE_WIN_PAGERANK3|S-|':'ET3',
  'PLACE|S1|':'P1', 'PLACE|S2|':'P2', 'PLACE|S4|':'P4', 'PLACE_S2_GE8|S2|':'P2-8',
  'S4_PLACE_WIN|S4|':'SW', 'S4_PLACE_WIN|S4|BSP_GE_6':'S6', 'S4_WIN_BRAIN1|S4|':'SB1', 'S4_WIN_PAGERANK3|S4|':'SP3',
  'AGREE_S4_PLACE_WIN|S4|':'AG', 'AGREE_ONBASE_BOOTSTRAP|S1|':'AG-B', 'RIVAL_S4_PAGERANK3|S4|':'RP',
  'LONGSHOT_TRAITS4|S-|':'LT4', 'BRAIN|refit-brain-max3-v1|WIN':'BR', 'BRAIN|daily3|WIN':'BD3',
  'FAV_STAKES_3PLUS|S-|':'F3', 'FAV_EXPOSED_MAIDEN|S-|':'FM', 'INRUN_STABLE5_FIRM20|S-|':'ST20',
};
const norm = (v: string) => v.toUpperCase().replace(/^\d+[.]?\s*/, '').replace(/[^A-Z0-9]/g, '');
const codeFor = (id: string) => codes[id] || id.split('|').filter(s => s && s !== 'S-').join('/');
type SummaryBet = BucketBet & { live_enabled?: boolean; currently_selected?: boolean | null };

const BucketRaceSummary: React.FC = () => {
  const [date, setDate] = useState(today);
  const [scope, setScope] = useState('paper');
  const [bets, setBets] = useState<SummaryBet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [updated, setUpdated] = useState('');
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!date) return;
      setLoading(true); setError('');
      try {
        const data = await getBucketBets('__race_summary__', date, date);
        if (active) { setBets(data.bets); setUpdated(new Date().toLocaleTimeString('en-AU', {timeZone:'Australia/Sydney'})); }
      } catch (e: any) { if (active) { setBets([]); setError(e.message || 'Could not load race summary'); } }
      finally { if (active) setLoading(false); }
    };
    setBets([]); setUpdated(''); void load();
    const timer = window.setInterval(() => {
      if (date === today() && document.visibilityState === 'visible') void load();
    }, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, [date, refresh]);

  const { races, legend } = useMemo(() => {
    const byRace = new Map<string, {track: string; race: number; picks: Map<string, {name:string; entries:Map<string, SummaryBet>}>}>();
    const labels = new Map<string, string>();
    for (const b of bets) {
      if (!b.track || b.race == null || !b.runner || !b.policy_id) continue;
      if (scope === 'paper' && b.live_enabled || scope === 'live' && !b.live_enabled) continue;
      const key = `${norm(b.track)}|${Number(b.race)}`;
      if (!byRace.has(key)) byRace.set(key, {track:b.track.toUpperCase(), race:Number(b.race), picks:new Map()});
      const race = byRace.get(key)!;
      const runner = norm(b.runner);
      if (!race.picks.has(runner)) race.picks.set(runner, {name:b.runner, entries:new Map()});
      race.picks.get(runner)!.entries.set(b.policy_id, b);
      labels.set(b.policy_id, b.bucket_label || b.policy_id);
    }
    return {
      races: [...byRace.values()].sort((a,b) => a.track.localeCompare(b.track) || a.race-b.race),
      legend: [...labels].sort((a,b) => codeFor(a[0]).localeCompare(codeFor(b[0]))),
    };
  }, [bets, scope]);

  return <section className="sr-card" aria-label="Daily race and bucket summary" style={{padding:16, marginTop:24}}>
    <h2>Daily race summary</h2>
    <div style={{display:'flex', gap:12, flexWrap:'wrap', alignItems:'center'}}>
      <label>Date <input aria-label="Race summary date" type="date" value={date} onChange={e=>setDate(e.target.value)} /></label>
      <button type="button" onClick={()=>setDate(today())}>Today</button>
      <button type="button" onClick={()=>{const d=new Date(`${today()}T12:00:00Z`); d.setUTCDate(d.getUTCDate()-1); setDate(d.toISOString().slice(0,10));}}>Yesterday</button>
      <label>Buckets <select aria-label="Race summary buckets" value={scope} onChange={e=>setScope(e.target.value)}>
        <option value="paper">Paper buckets</option><option value="all">All buckets</option><option value="live">Live-enabled buckets</option>
      </select></label>
      <button type="button" disabled={loading || !date} onClick={()=>setRefresh(x=>x+1)}>Refresh</button>
    </div>
    <p className="rc-note">{date} · sorted by venue, then race number. Earlier recorded picks remain visible. Bucket mode follows its current switch. {updated && `Updated ${updated} Sydney time.`}</p>
    {loading && <p role="status">Loading race summary…</p>}
    {error && <p className="sr-error" role="alert">{error}</p>}
    {!loading && !error && !races.length && <p>No recorded bucket picks for this date.</p>}
    {races.length > 0 && <table className="sr-table mobile-card-table">
      <thead><tr><th>Track</th><th>Race</th><th>Picks — bucket abbreviations and results</th></tr></thead>
      <tbody>{races.map(r=><tr key={`${r.track}|${r.race}`}>
        <td data-label="Track"><strong>{r.track}</strong></td><td data-label="Race">{r.race}</td>
        <td data-label="Picks">{[...r.picks.values()].sort((a,b)=>a.name.localeCompare(b.name)).map(p=><div key={p.name} style={{marginBottom:6}}>
          <strong>{p.name}</strong> — {[...p.entries].sort((a,b)=>codeFor(a[0]).localeCompare(codeFor(b[0]))).map(([id,b],i)=><React.Fragment key={id}>
            {i>0 && ', '}<span title={`${b.bucket_label || id} · ${b.market_type} · ${b.side}${b.bsp ? ` · BSP $${b.bsp.toFixed(2)}` : ''}`}>
              {codeFor(id)}{b.result==='win'?' ✓':b.result==='lose'?' ✗':b.result==='void'?' (void)':b.currently_selected===false && Date.parse(b.start_time || '')>Date.now()?' (earlier)':''}
            </span>
          </React.Fragment>)}
        </div>)}</td>
      </tr>)}</tbody>
    </table>}
    <p className="rc-note">✓ Won · ✗ Lost · (void) excluded / unfilled · unmarked results pending. WIN and PLACE outcomes stay separate beside their bucket code.</p>
    {legend.length>0 && <details open><summary>Bucket abbreviation key</summary>
      <table className="sr-table"><thead><tr><th>Abbreviation</th><th>Bucket</th></tr></thead><tbody>
        {legend.map(([id,label])=><tr key={id}><td>{codeFor(id)}</td><td>{label}</td></tr>)}
      </tbody></table>
    </details>}
  </section>;
};
export default BucketRaceSummary;
